/* Domain dependencies */
import { boxModel } from '@/domain/box/box.model';

/* Application dependencies */
import { getHeadquartersExecute } from '@/application/box/get-headquarters.use-case';
import { listBoxesExecute } from '@/application/common/list-boxes.use-case';
import { listPrioritiesExecute } from '@/application/common/list-priorities.use-case';
import { listAttentionQueuesExecute } from '@/application/common/list-attention-queues.use-case';
import {
  saveAssignmentExecute,
  updateTicketExecute,
} from '@/application/box/save-assignment.use-case';

/* Infrastructure dependencies */
import {
  apiGetHeadquartersList,
  apiGetPriorityList,
  apiGetAttentionQueueList,
  apiRegisterBox,
  apiGetBoxesList,
  apiUpdateTicket,
} from '@/infrastructure/apis/attention-queues';
import { selectMessage } from '@/infrastructure/utils/modals';
import {
  API_HEADQUARTERS_LIST_MESSAGES,
  API_PRIORITY_LIST_MESSAGES,
  API_ATTENTION_QUEUE_LIST_MESSAGES,
  API_REGISTER_BOX_MESSAGES,
  API_BOXES_LIST_MESSAGES,
} from '@/infrastructure/apis/messages/attention-queues.message';
import router from '@/infrastructure/vue/router';
import { PATHS } from '@/infrastructure/utils/route-constants';

const namespaced = true;

const state = () => ({
  box: boxModel,
});

const getters = {
  headquartersList: (state) => state.box.headquartersList,
  boxList: (state) => state.box.boxList,
  priorityList: (state) => state.box.priorityList,
  queueList: (state) => state.box.queueList,
  ticketList: (state) =>
    state.box.ticketList.length > 0
      ? state.box.ticketList.sort((a, b) => a.orderNumber - b.orderNumber)
      : [],
  assignmentDetail: (state) => state.box.assignmentDetail,
};

const mutations = {
  SET_HEADQUARTERS_LIST(state, data) {
    state.box.headquartersList = data;
  },
  SET_BOXES_LIST(state, data) {
    state.box.boxList = data;
  },
  SET_PRIORITY_LIST(state, data) {
    state.box.priorityList = data;
  },
  SET_QUEUE_LIST(state, data) {
    state.box.queueList = data;
  },
  SET_TICKETS_LIST(state, data) {
    state.box.ticketList = data;
  },
  SET_ASSIGNMENT_DETAIL(state, data) {
    state.box.assignmentDetail = data;
  },
};

const actions = {
  async headquartersListAction({ commit, dispatch }) {
    try {
      const { headquartersList } = await getHeadquartersExecute(
        apiGetHeadquartersList
      );
      commit('SET_HEADQUARTERS_LIST', headquartersList);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_HEADQUARTERS_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async boxListAction({ commit, dispatch }, payload) {
    try {
      const { listBoxes } = await listBoxesExecute(payload, apiGetBoxesList);
      commit('SET_BOXES_LIST', listBoxes);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_BOXES_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async priorityListAction({ commit, dispatch }) {
    try {
      const { listPriorities } = await listPrioritiesExecute(
        apiGetPriorityList
      );
      commit('SET_PRIORITY_LIST', listPriorities);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_PRIORITY_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async queueListAction({ rootState, commit, dispatch }) {
    try {
      const { listAttentionQueues } = await listAttentionQueuesExecute(
        rootState.login.user.attentionType,
        apiGetAttentionQueueList
      );
      commit('SET_QUEUE_LIST', listAttentionQueues);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_ATTENTION_QUEUE_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async saveAssignmentAction({ rootState, commit, dispatch }, payload) {
    try {
      await saveAssignmentExecute(payload, apiRegisterBox);
      commit('SET_ASSIGNMENT_DETAIL', {
        arrayAttentionQueue: payload.attentionQueue,
        arrayAttentionType: payload.priority,
        boxCode: payload.box,
        headquartersCode: payload.headquarter,
        userCode: rootState.login.user.userCode,
        attentionType: rootState.login.user.attentionType,
      });
      commit(
        'login/SET_HEADQUARTERS_DATA',
        {
          headquartersCode: payload.headquarter,
          headquartersShort: payload.headquartersShort,
        },
        { root: true }
      );
      router.push(PATHS.box.attentionBox);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_REGISTER_BOX_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async updateTicketAction({ dispatch }, payload) {
    try {
      await updateTicketExecute(payload, apiUpdateTicket);
      return true;
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_REGISTER_BOX_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
      return false;
    }
  },
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};
